/* <surisa-volume-pricing> — tarjeta de precio con descuentos por volumen (PDP)
   Lee los tramos desde un <script type="application/json" data-tiers> (emitido
   por la sección desde el metaobject volume_discount_scheme), recalcula
   precio/ud + total + tramo activo en vivo según la cantidad, y mantiene el
   label del botón Añadir al carrito.

   Contrato data-attrs:
   - data-base-price  : precio base del variant en céntimos (entero)
   - data-moq         : cantidad mínima de pedido
   - data-unit-plural : sustantivo de la unidad de venta en las filas de tramos
                        ("sobres", "cajas"). Sale de custom.supply_unit_plural.
                        Vacío/ausente → "ud", el literal de siempre.
   - data-unit-suffix : sufijo del precio por unidad en las filas de tramos
                        ("/ sobre de 10 uds."). Sale del mismo cálculo que el
                        precio principal (custom.supply_format + el setting
                        per_unit_format_label). Vacío/ausente → "/ ud".
   Estructura interna esperada: [data-qty] input, [data-unit-price],
   [data-total], [data-tiers-list], [data-tiers-toggle], botones name=minus/plus.

   El elemento se reconstruye en cada cambio de variante (product-info re-render),
   así que connectedCallback re-inicializa con el variant nuevo. */
(function () {
  if (customElements.get('surisa-volume-pricing')) return;

  var fmt = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  function euros(cents) { return '€ ' + fmt.format(cents / 100); }

  /* Los rótulos de unidad vienen de metafields del producto y las filas de
     tramos se montan con innerHTML, así que hay que reescapar: getAttribute
     devuelve el texto ya decodificado (el `escape` de Liquid actúa sobre el
     atributo, no sobre lo que lee la JS). */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Rótulo con fallback al literal histórico: un producto sin el metafield
     renderiza exactamente igual que antes de que existiera. */
  function labelAttr(el, name, fallback) {
    var v = el.getAttribute(name);
    v = v == null ? '' : v.trim();
    return v === '' ? fallback : v;
  }

  customElements.define('surisa-volume-pricing', class extends HTMLElement {
    connectedCallback() {
      this.base = parseInt(this.getAttribute('data-base-price'), 10) || 0;
      this.moq = parseInt(this.getAttribute('data-moq'), 10) || 1;
      this.stock = parseInt(this.getAttribute('data-stock'), 10);  // NaN = sin tope de stock
      this.unitPlural = labelAttr(this, 'data-unit-plural', 'ud');
      this.unitSuffix = labelAttr(this, 'data-unit-suffix', '/ ud');
      this.tiers = this._readTiers();           // [{min, pct}] ordenado asc por min
      this.qtyInput = this.querySelector('[data-qty]');
      this.unitEl = this.querySelector('[data-unit-price]');
      this.totalEl = this.querySelector('[data-total]');
      this.listEl = this.querySelector('[data-tiers-list]');
      this.addBtn = this.querySelector('.product-form__submit');
      this.addBtnBase = this.addBtn ? (this.addBtn.querySelector('span') ? this.addBtn.querySelector('span').textContent.trim() : this.addBtn.textContent.trim()) : '';

      this._bind();

      // Sin esquema de volumen: ocultar la sección de tramos por completo.
      // style.display='none' vence al display:flex del toggle (el atributo
      // [hidden] no basta — GOTCHAS Frontend §2: CSS display vence a [hidden]).
      if (this.tiers.length === 0) {
        var toggle = this.querySelector('[data-tiers-toggle]');
        if (toggle) toggle.style.display = 'none';
        if (this.listEl) this.listEl.style.display = 'none';
      } else {
        this._renderTiers();
      }
      this._applyUrlQuantity();
      this._update();
    }

    /* Precarga la cantidad desde el query param `?quantity=N` de la URL — el CTA
       "Comprar" del catálogo (page-catalogo) enlaza a /products/<h>?variant=<id>&quantity=<N>
       con la cantidad que el usuario fijó en la tabla. Sin esto, la PDP arrancaba
       siempre en el MOQ e ignoraba lo elegido. Se capa a [moq, stock] igual que el
       stepper. No se toca si el input está readonly/disabled (consulta/sinstock). */
    _applyUrlQuantity() {
      if (!this.qtyInput || this.qtyInput.readOnly || this.qtyInput.disabled) return;
      var params;
      try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
      if (!params.has('quantity')) return;
      var q = parseInt(params.get('quantity'), 10);
      if (isNaN(q)) return;
      if (q < this.moq) q = this.moq;
      if (!isNaN(this.stock) && q > this.stock) q = this.stock;
      this.qtyInput.value = String(q);
    }

    _readTiers() {
      var node = this.querySelector('[data-tiers]');
      if (!node) return [];
      try {
        var arr = JSON.parse(node.textContent.trim() || '[]');
        return arr
          .filter(function (t) { return t && t.min != null; })
          .map(function (t) { return { min: Number(t.min), pct: Number(t.pct) || 0 }; })
          .sort(function (a, b) { return a.min - b.min; });
      } catch (e) { return []; }
    }

    _bind() {
      var self = this;
      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', function () { self._update(); });
        this.qtyInput.addEventListener('change', function () { self._clampAndUpdate(); });
      }
      var minus = this.querySelector('button[name="minus"]');
      var plus = this.querySelector('button[name="plus"]');
      if (minus) minus.addEventListener('click', function () { self._step(-1); });
      if (plus) plus.addEventListener('click', function () { self._step(1); });

      var toggle = this.querySelector('[data-tiers-toggle]');
      if (toggle) toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        if (self.listEl) self.listEl.hidden = open;
      });

      // Garantía de tope de stock: capar la cantidad en el submit del formulario
      // de compra (fase de captura → corre ANTES del handler de product-form.js,
      // que lee el FormData). Así, si el usuario pide más unidades de las
      // disponibles, se añade el máximo y el popup confirma esa cifra en vez de
      // fallar con un 422 (que no mostraría el popup).
      if (!isNaN(this.stock) && this.qtyInput && this.qtyInput.form) {
        var form = this.qtyInput.form;
        if (!form.__surisaStockClamp) {
          form.__surisaStockClamp = true;
          form.addEventListener('submit', function () {
            var v = parseInt(self.qtyInput.value, 10);
            if (!isNaN(self.stock) && !isNaN(v) && v > self.stock) {
              self.qtyInput.value = String(self.stock);
              self._update();
            }
          }, true);
        }
      }
    }

    _step(dir) {
      var v = (parseInt(this.qtyInput.value, 10) || this.moq) + dir;
      if (v < this.moq) v = this.moq;
      if (!isNaN(this.stock) && v > this.stock) v = this.stock;
      this.qtyInput.value = String(v);
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _clampAndUpdate() {
      var v = parseInt(this.qtyInput.value, 10);
      if (isNaN(v) || v < this.moq) { v = this.moq; }
      if (!isNaN(this.stock) && v > this.stock) { v = this.stock; }
      this.qtyInput.value = String(v);
      this._update();
    }

    /* Tramo aplicable: el de mayor `min` que no supere qty. Si qty < primer min → base. */
    _tierFor(qty) {
      var applicable = null;
      for (var i = 0; i < this.tiers.length; i++) {
        if (qty >= this.tiers[i].min) applicable = this.tiers[i];
      }
      return applicable; // null = precio base
    }

    _unitFor(tier) {
      if (!tier || !tier.pct) return this.base;
      return Math.round(this.base * (1 - tier.pct / 100));
    }

    _update() {
      var qty = parseInt(this.qtyInput ? this.qtyInput.value : this.moq, 10) || this.moq;
      var tier = this._tierFor(qty);
      var unit = this._unitFor(tier);
      var total = unit * qty;

      if (this.unitEl) this.unitEl.textContent = euros(unit);
      if (this.totalEl) this.totalEl.textContent = euros(total);
      if (this.addBtn) {
        var span = this.addBtn.querySelector('span') || this.addBtn;
        span.textContent = this.addBtnBase + ' · ' + euros(total);
      }
      this._markActive(tier);
    }

    _renderTiers() {
      if (!this.listEl) return;
      var rows = [];
      // Fila base
      var firstMin = this.tiers.length ? this.tiers[0].min : null;
      var baseLabel = firstMin ? ('1–' + (firstMin - 1)) : '1+';
      rows.push(this._row('base', baseLabel + ' ' + this.unitPlural, 0, this.base));
      // Tramos
      for (var i = 0; i < this.tiers.length; i++) {
        var t = this.tiers[i];
        var next = this.tiers[i + 1];
        var label = next ? (t.min + '–' + (next.min - 1)) : (t.min + '+');
        rows.push(this._row(String(t.min), label + ' ' + this.unitPlural, t.pct, this._unitFor(t)));
      }
      this.listEl.innerHTML = rows.join('');
    }

    /* Los tres datos del tramo (cantidad · descuento · precio por unidad)
       son HERMANOS, no anidados. El descuento vivía dentro de
       .pdp__tier-qty y en móvil eso los pegaba: la columna de cantidad
       envolvía en dos líneas y el «−10%» quedaba a 0px del «… uds.» del
       precio, que también envolvía — se leía «-10%uds.» de corrido. Como
       hermanos, cada uno ocupa su celda de la rejilla y el CSS los coloca
       en una línea (desktop) o en dos (móvil). */
    _row(key, label, pct, unitCents) {
      var off = pct > 0 ? '<span class="pdp__tier-off">−' + pct + '%</span>' : '';
      return '<div class="pdp__tier" data-tier="' + key + '" data-active="false">' +
        '<span class="pdp__tier-qty">' + esc(label) + '</span>' +
        off +
        '<span class="pdp__tier-price">' + euros(unitCents) + ' <span>' + esc(this.unitSuffix) + '</span></span>' +
        '</div>';
    }

    _markActive(tier) {
      if (!this.listEl) return;
      var key = tier ? String(tier.min) : 'base';
      var nodes = this.listEl.querySelectorAll('.pdp__tier');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].setAttribute('data-active', String(nodes[i].getAttribute('data-tier') === key));
      }
    }
  });
})();
